import { useFormContext, useWatch } from 'react-hook-form';
import ConversionBase from '../core/form/ConversionBase';
import InputBase from '../core/form/InputBase';
import SelectBase from '../core/form/SelectBase';
import PropTypes from 'prop-types';
import ErrorMessage from '../parameters/fields/ErrorMessage';
import FormRow from '../core/form/FormRow';
import ToggleBase from '../core/form/ToggleBase';
import { Fragment, useMemo, useEffect } from 'react';
import { keyBy, stringify } from '@/utils/miscUtils';
import { parseUnitsWithDenominators, parseUnitsWithNumerators } from '@/utils/unitUtils';
import get from 'just-safe-get';

const COST_SPECIFICATION_OPTIONS = [
  { label: 'Full Price', value: 'full' },
  { label: 'Process Price Only', value: 'process_only' },
];

const TeaInputs = ({ typeToUnitsMap }) => {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext();

  const outputs = useWatch({ name: 'outputs' });
  const costType = useWatch({ name: 'tea_type' }) || 'simple';
  const referenceProduct = useWatch({ name: 'bottomUpCosts.reference_product' });
  const showScheduledReplacement = useWatch({ name: 'bottomUpCosts.add_scheduled_replacement' });
  const showExponentialCostScaling = useWatch({ name: 'bottomUpCosts.exponential_cost_scaling' });
  const nonEmissionOutputs = useMemo(() => {
    if (!outputs) return [];

    const filtered = outputs.filter(output => output.emissions === false);
    return filtered.map(output => ({
      ...output,
      unit: output?.unit ? parseUnitsWithNumerators([JSON.parse(output.unit)])?.[0] : null,
    }));
  }, [outputs]);

  // set levelized_costs in form state based on the current nonEmissionOutputs
  useEffect(() => {
    const currentLevelizedCosts = nonEmissionOutputs.map(output => ({
      product: output.name,
      cost: {
        scalar: 0,
        unit: stringify(output.unit),
      },
    }));

    setValue('simpleCosts.levelized_costs', currentLevelizedCosts);
  }, [nonEmissionOutputs, setValue]);

  const nonEmissionOutputsByProduct = keyBy(nonEmissionOutputs, 'name');
  const chosenOutput = nonEmissionOutputsByProduct[referenceProduct];
  const referenceCapacityUnits = parseUnitsWithDenominators(typeToUnitsMap?.[chosenOutput?.species] || []);
  const defaultUnit = referenceCapacityUnits[0];
  const bottomUpParams = [
    {
      id: 'reference_product',
      label: 'Reference Product',
      Component: SelectBase,
      componentProps: {
        options: nonEmissionOutputs.map(output => ({ label: output.label, value: output.name })),
        allowNull: true,
      },
    },
    {
      id: 'reference_capacity',
      label: 'Reference Capacity',
      defaultUnit: stringify(defaultUnit),
      units: referenceCapacityUnits,
      Component: InputBase,
      componentProps: {
        disabled: !referenceProduct || costType !== 'dcfa',
      },
    },
    {
      id: 'capacity_factor',
      label: 'Capacity Factor',
      Component: InputBase,
      readOnlyUnit: '%',
    },
    {
      id: 'capital_cost',
      label: 'Capital Cost',
      Component: InputBase,
      readOnlyUnit: '$',
    },
    {
      id: 'fixed_operating_costs',
      label: 'Fixed Operating Costs',
      Component: InputBase,
      readOnlyUnit: '$/year',
    },
    {
      id: 'fixed_maintenance_costs',
      label: 'Fixed Maintenance Costs',
      Component: InputBase,
      readOnlyUnit: '$/year',
    },
    {
      id: 'variable_operating_costs',
      label: 'Variable Operating Costs',
      readOnlyUnit: '$/kg',
      Component: InputBase,
    },
    {
      id: 'add_scheduled_replacement',
      label: 'Add scheduled replacement costs?',
      Component: ToggleBase,
      componentProps: {
        onChange: e => setValue('bottomUpCosts.add_scheduled_replacement', e.target.checked),
        defaultChecked: true,
      },
    },
    ...(showScheduledReplacement
      ? [
          {
            id: 'scheduled_replacement_costs.replacement_costs',
            label: 'Schedule Replacement Costs',
            readOnlyUnit: '$',
            Component: InputBase,
            componentProps: {},
          },
          {
            id: 'scheduled_replacement_costs.replacement_frequency',
            label: 'Replacement Frequency',
            readOnlyUnit: 'years',
            Component: InputBase,
          },
        ]
      : []),
    {
      id: 'exponential_cost_scaling',
      label: 'Exponential Cost Scaling?',
      Component: ToggleBase,
      componentProps: {
        onChange: e => setValue('bottomUpCosts.exponential_cost_scaling', e.target.checked),
        defaultChecked: true,
      },
    },
    ...(showExponentialCostScaling
      ? [
          {
            id: 'scaling_exponent',
            label: 'Scaling Exponent',
            Component: InputBase,
          },
        ]
      : []),
  ];

  const handleCostTypeChange = e => {
    setValue('tea_type', e.target.value);
  };

  return (
    <div>
      <p className="text-md font-medium text-center mb-6 text-gray-400">Part 2: TEA</p>
      <div className="grid grid-cols-4 gap-6">
        <div className="col-span-1 p-4 group node border-r border-gray-200">
          <label className="flex items-center gap-2 cursor-pointer mb-4">
            <input
              type="radio"
              className="radio radio-primary radio-xs"
              value="simple"
              checked={costType === 'simple'}
              onChange={handleCostTypeChange}
            />
            <span className="label-text">Simple Cost</span>
          </label>
          <div>
            <FormRow label="Cost Specification" name="simpleCosts.spec_type">
              <SelectBase
                {...register('simpleCosts.spec_type', {
                  required: costType === 'simple' ? 'Cost specification is required' : false,
                })}
                options={COST_SPECIFICATION_OPTIONS}
                disabled={costType !== 'simple'}
                hasError={!!get(errors, 'simpleCosts.spec_type')}
              />
            </FormRow>
            <div className="h-4">
              <ErrorMessage className="pt-0 m-0 pl-0" text={get(errors, 'simpleCosts.spec_type.message')} />
            </div>
            {nonEmissionOutputs.map((output, index) => (
              <Fragment key={`no-emission-output-${index}`}>
                <FormRow label={`${output.label} Cost`} name={`simpleCosts.levelized_costs.${index}`}>
                  <input
                    type="hidden"
                    {...register(`simpleCosts.levelized_costs.${index}.product`)}
                    value={output.name}
                  />
                  <div className="join w-full">
                    <InputBase
                      {...register(`simpleCosts.levelized_costs.${index}.cost.scalar`, {
                        required: costType === 'simple' ? 'Type is required' : false,
                        setValueAs: value => (value === '' ? undefined : +value),
                      })}
                      disabled={costType !== 'simple'}
                      hasError={!!errors?.simpleCosts?.levelized_costs?.[index]?.cost?.scalar}
                    />
                    <ConversionBase
                      name={`simpleCosts.levelized_costs.${index}.cost.unit`}
                      value={output.unit}
                      className="w-fit"
                      units={parseUnitsWithNumerators(typeToUnitsMap[output.species])}
                      disabled={costType !== 'simple'}
                    />
                  </div>
                </FormRow>
                <div className="h-4">
                  <ErrorMessage
                    className="pt-0 m-0 pl-0"
                    text={errors?.simpleCosts?.levelized_costs?.[index]?.cost?.scalar?.message}
                  />
                </div>
              </Fragment>
            ))}
          </div>
        </div>

        <div className="col-span-3 py-4 group node">
          <label className="flex items-center gap-2 cursor-pointer mb-4">
            <input
              type="radio"
              className="radio radio-primary radio-xs"
              value="dcfa"
              checked={costType === 'dcfa'}
              onChange={handleCostTypeChange}
            />
            <span className="label-text">Bottom-up Cost Analysis</span>
          </label>
          <div className="grid grid-cols-3 gap-4">
            {bottomUpParams.map(({ id, label, defaultUnit, units, readOnlyUnit, Component, componentProps }) => (
              <div key={id} className="col-span-1">
                <FormRow label={label} name={`bottomUpCosts.${id}`}>
                  <div className="join w-full">
                    <Component
                      {...register(`bottomUpCosts.${id}`, {
                        required: costType === 'dcfa' && Component !== ToggleBase ? 'This field is required' : false,
                        ...(Component === InputBase
                          ? {
                              setValueAs: value => (value === '' ? undefined : +value),
                            }
                          : {}),
                      })}
                      disabled={costType !== 'dcfa'}
                      hasError={!!get(errors, `bottomUpCosts.${id}`)}
                      {...(componentProps || {})}
                    />
                    {defaultUnit && (
                      <ConversionBase
                        name={`bottomUpCosts.conversions.${id}`}
                        value={defaultUnit}
                        units={units}
                        readOnly={costType !== 'dcfa'}
                      />
                    )}
                    {readOnlyUnit && (
                      <div className={`input input-sm ${costType !== 'dcfa' ? 'input-disabled' : 'input-bordered'}`}>
                        {readOnlyUnit}
                      </div>
                    )}
                  </div>
                </FormRow>
                <div className="h-5">
                  <ErrorMessage text={get(errors, `bottomUpCosts.${id}.message`)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

TeaInputs.propTypes = {
  typeToUnitsMap: PropTypes.object.isRequired,
};

export default TeaInputs;
